import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { AiOutlineWarning } from "react-icons/ai";
import axios from "../../../api/axios";
import {
  BILLING_DETAILS_CONFIG_URL,
  ORDER_DETAILS_CONFIG_URL,
  CART_DETAILS_CONFIG_URL,
  RSA_KEY_URL,
} from "../../../api/api_routing_urls";

import {
  setCartQuantity,
  resetCartQuantity,
} from "../../../features/cartQuantity/cartQuantitySlice";

import DeleteModal from "../../../reusable-components/modals/DeleteModal";
import BillingAddressCard from "../../../reusable-components/billing-address-card/billingAddressCard.component";
import OrderSuccessPage from "./order_success_page.component";
import BillingDetailsForm from "./billing_details_form.component";
import Spinner from "../../../reusable-components/spinner/spinner.component";
import moment from "moment";

import rsaEncryption from "../../../utilities/rsaFunction/rsaEncryption";

const Checkout = ({ cartList, setShowCheckoutPage }) => {
  const dispatch = useDispatch();
  const { rsaEncrypt } = rsaEncryption;

  const auth = useSelector((state) => state.authProvider.auth);
  const user_id = auth?.user_id;

  const deliverycharge = 100;

  const [cartTotal, setCartTotal] = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);

  const [billingDetailsList, setBillingDetailsList] = useState([]);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBillingId, setSelectedBillingId] = useState(null);

  const [showDelete, setShowDelete] = useState(false);
  const [billingDetailsDeleteId, setBillingDetailsDeleteId] = useState(null);

  const [showOrderSuccessPage, setShowOrderSuccessPage] = useState(false);
  const [processingPaymentMsg, setProcessingPaymentMsg] = useState(false);

  const [editBillingAddressDetails, setEditBillingAddressDetails] = useState(
    {}
  );

  // COD only for now.
  // Future options:
  // - To support both COD + Online again, change default to "Online" or keep dynamic,
  //   and uncomment the payment mode selection UI in the JSX below.
  // - To make Online-only in future, set:
  //     const [paymentMode, setPaymentMode] = useState("Online");
  //   and keep the COD success branch disabled in UI if needed.
  const [paymentMode, setPaymentMode] = useState("COD"); // "Online" | "COD"

  const isEdit = Object.keys(editBillingAddressDetails || {}).length > 0;

  const defaultValues = useMemo(
    () => ({
      username: " ",
      password: " ",
      first_name: !isEdit ? "" : editBillingAddressDetails?.first_name || "",
      last_name: !isEdit ? "" : editBillingAddressDetails?.last_name || "",
      address_line1: !isEdit
        ? ""
        : editBillingAddressDetails?.address_line1 || "",
      address_line2: !isEdit
        ? ""
        : editBillingAddressDetails?.address_line2 || "",
      landmark: !isEdit ? "" : editBillingAddressDetails?.landmark || "",
      city_name: !isEdit ? "" : editBillingAddressDetails?.city_name || "",
      state_name: !isEdit ? "" : editBillingAddressDetails?.state_name || "",
      pincode: !isEdit ? "" : editBillingAddressDetails?.pincode || "",
      user_email: !isEdit ? "" : editBillingAddressDetails?.user_email || "",
      phone_number: !isEdit
        ? ""
        : editBillingAddressDetails?.phone_number || "",
    }),
    [isEdit, editBillingAddressDetails]
  );

  const getBillingDetailsList = async (userId) => {
    try {
      const response = await axios.get(BILLING_DETAILS_CONFIG_URL, {
        params: { user_id: userId },
      });

      if (response.status === 200) {
        const list = response?.data?.billingDetailsList || [];
        setBillingDetailsList(list);

        setSelectedBillingId((prev) => {
          if (prev && list.some((item) => item.billing_id === prev)) return prev;
          return list[0]?.billing_id || null;
        });
      } else if (response.status === 202) {
        setBillingDetailsList([]);
        setSelectedBillingId(null);
        console.log("Billing details not found.");
      }
    } catch (error) {
      console.error(error);
      if (!error?.response) {
        console.log("No Server Response");
      } else if (error.response.status === 422) {
        console.log("Some of the required inputs were not provided.");
      } else {
        console.log(
          "Whoops!!!! This doesn't feel right. There might be an issue. Please contact the administrator."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotal = () => {
    let ctotal = 0;

    cartList?.forEach((item) => {
      const rateAfterDiscount =
        item?.product_rate -
        (item?.discount_percentage / 100) * item?.product_rate;

      ctotal += item.quantity * rateAfterDiscount;
    });

    const ototal = ctotal + deliverycharge;

    setCartTotal(ctotal);
    setOrderTotal(ototal);
  };

  const onBillingClickHandler = (selectedId) => {
    setSelectedBillingId(selectedId);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    reset,
    setValue,
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onClickDelete = async () => {
    try {
      let response = null;

      if (billingDetailsDeleteId) {
        response = await axios.post(`${BILLING_DETAILS_CONFIG_URL}/delete`, {
          user_id,
          billing_id: billingDetailsDeleteId,
        });
        setShowDelete(false);
      }

      if (response?.status === 200) {
        console.log("User billing details deleted successfully.");
        await getBillingDetailsList(user_id);
      } else {
        console.log("User billing details deletion failed.");
      }
    } catch (error) {
      console.log("Delete Module Error", error);
    } finally {
      setBillingDetailsDeleteId(null);
    }
  };

  const onClickEdit = (billingObj) => {
    setEditBillingAddressDetails(billingObj || {});
    setShowAddAddressForm(true);
  };

  const onClickAdd = () => {
    setEditBillingAddressDetails({});
    setShowAddAddressForm(true);
  };

  const onSubmitForm = async (data) => {
    try {
      const sendDataObj = {
        user_id,
        first_name: data.first_name,
        last_name: data.last_name,
        address_line1: data.address_line1,
        address_line2: data.address_line2,
        landmark: data.landmark,
        city_name: data.city_name,
        state_name: data.state_name,
        pincode: data.pincode,
        user_email: data.user_email,
        phone_number: data.phone_number,
      };

      let response = null;

      if (!isEdit) {
        response = await axios.post(BILLING_DETAILS_CONFIG_URL, sendDataObj);
      } else {
        response = await axios.post(`${BILLING_DETAILS_CONFIG_URL}/update`, {
          ...sendDataObj,
          billing_id: editBillingAddressDetails?.billing_id,
        });
      }

      if (response.status === 200) {
        if (isEdit) {
          console.log("User billing details updated successfully.");
        } else {
          console.log("User billing details added successfully.");
        }

        setShowAddAddressForm(false);
        setEditBillingAddressDetails({});
        reset();
        await getBillingDetailsList(user_id);
      } else {
        console.log(
          "Whoops!!!! This doesn't feel right. There might be an issue. Please contact the administrator."
        );
      }
    } catch (error) {
      console.error("error", error);
      if (!error?.response) {
        console.log("No Server Response");
      } else if (error.response.status === 422) {
        console.log("Some of the required inputs were not provided.");
      } else {
        console.log(
          "Whoops!!!! This doesn't feel right. There might be an issue. Please contact the administrator."
        );
      }
    }
  };

  const getPublicKeyRsa = async () => {
    try {
      const response = await axios.get(RSA_KEY_URL);
      if (response?.data?.status === 201) {
        return {
          key_id: {
            Public_Key: response?.data?.publicKey,
            Session_Uuid: response?.data?.session_uuid,
          },
        };
      }
      return { key_id: null };
    } catch (err) {
      console.error(err);
      return { key_id: null };
    }
  };

  const requestOrderPaymentAPI = async (ORDERNO, amount) => {
    try {
      const { key_id } = await getPublicKeyRsa();

      if (!key_id?.Public_Key || !key_id?.Session_Uuid) {
        throw new Error("RSA key/session not received.");
      }

      const params = {
        order_id: rsaEncrypt(ORDERNO, key_id.Public_Key),
        amount: rsaEncrypt(amount, key_id.Public_Key),
        sessionUuid: key_id.Session_Uuid,
      };

      const response = await axios.post("/ccavRequestHandler", params);

      const responseContainer = document.createElement("div");
      responseContainer.innerHTML = response.data;

      document.body.appendChild(responseContainer.firstChild);

      const paymentForm = document.getElementById("nonseamless");
      if (paymentForm) {
        paymentForm.submit();
      } else {
        throw new Error("Payment form not found in response.");
      }
    } catch (error) {
      console.error("ccavRequestHandler", error);
      throw error;
    }
  };

  const getCartItemsQuantityDetails = async (userId) => {
    try {
      const response = await axios.get(
        `${CART_DETAILS_CONFIG_URL}/getTotalItems`,
        {
          params: { user_id: userId },
        }
      );

      if (response.status === 200) {
        dispatch(setCartQuantity(+response?.data?.cartItemsQuantityDetails || 0));
      } else if (response.status === 202) {
        console.log("No details found in the system.");
      }
    } catch (error) {
      console.error("getCartItemsQuantityDetails", error);
      if (!error?.response) {
        console.log("No Server Response");
      } else if (error.response.status === 422) {
        console.log("Some of the required inputs were not provided.");
      } else {
        console.log(
          "Whoops!!!! This doesn't feel right. There might be an issue. Please contact the administrator."
        );
      }
    }
  };

  const onPlaceOrderClickHandler = async () => {
    if (!selectedBillingId || processingPaymentMsg) return;

    if (!orderTotal || orderTotal <= 0) {
      console.log("Invalid order total.");
      return;
    }

    setProcessingPaymentMsg(true);

    try {
      const timestamp = moment().format("YYYYMMDDHHmmss");
      const ORDERNO = `${user_id}${timestamp}`;

      const prd_details =
        cartList?.map((item) => ({
          cart_id: item.cart_id,
          product_id: item.product_details.product_id,
          product_name: item.product_details.product_name,
          product_rate: item.product_rate,
          discount_percentage: item.discount_percentage,
          size_variety_id: item.product_details.size_variety_id,
          size_name: item.size_name,
          quantity: item.quantity,
          amount_paid: paymentMode === "COD" ? 0 : orderTotal,
        })) || [];

      const orderDataObj = {
        user_id,
        billing_id: selectedBillingId,
        payment_mode: paymentMode,
        product_details: JSON.stringify(prd_details),
        order_no: ORDERNO,
        order_total: orderTotal,
      };

      const response = await axios.post(ORDER_DETAILS_CONFIG_URL, orderDataObj);

      if (response.status !== 200) {
        console.log(
          "Whoops!!!! This doesn't feel right. There might be an issue. Please contact the administrator."
        );
        return;
      }

      dispatch(resetCartQuantity());
      await getCartItemsQuantityDetails(user_id);

      // Online flow retained for future use.
      // To enable Online payments again:
      // 1. Uncomment the payment mode selection UI below.
      // 2. Set default paymentMode as needed.
      // 3. Keep this branch active.
      if (paymentMode === "Online") {
        await requestOrderPaymentAPI(ORDERNO, parseFloat(orderTotal).toFixed(2));
      } else {
        setShowOrderSuccessPage(true);
      }
    } catch (error) {
      console.error("error", error);
      if (!error?.response) {
        console.log("No Server Response");
      } else if (error.response.status === 422) {
        console.log("Some of the required inputs were not provided.");
      } else {
        console.log(
          "Whoops!!!! This doesn't feel right. There might be an issue. Please contact the administrator."
        );
      }
    } finally {
      setProcessingPaymentMsg(false);
    }
  };

  useEffect(() => {
    if (user_id) {
      getCartItemsQuantityDetails(user_id);
    }
  }, [user_id]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    if (user_id) {
      getBillingDetailsList(user_id);
    }
  }, [user_id]);

  useEffect(() => {
    calculateTotal();
  }, [cartList]);

  if (showOrderSuccessPage) {
    return (
      <div>
        <OrderSuccessPage />
      </div>
    );
  }

  return (
    <div className="font-tenorsans">
      <div className="grid grid-cols-2 md:grid-cols-3 items-center">
        <div className="md:col-start-2 flex justify-start md:justify-center text-sm md:text-lg lg:text-xl py-4">
          Checkout
        </div>
        <div
          onClick={() => setShowCheckoutPage(false)}
          className="flex justify-end lg:justify-center text-[10px] md:text-xs text-black uppercase cursor-pointer py-4"
        >
          Back to Cart
        </div>
      </div>

      <div>
        <section className="py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 md:gap-8">
            <div className="col-span-2">
              <div className="relative flex items-center mb-2">
                <span className="flex-shrink mr-4 text-sm lg:text-base uppercase">
                  Billing Details
                </span>
                <div className="flex-grow border-t border-gray-400" />
              </div>

              <div>
                {!isLoading &&
                billingDetailsList?.length > 0 &&
                showAddAddressForm === false ? (
                  <>
                    <div className="uppercase text-xs lg:text-sm py-2 lg:py-3">
                      Select a billing address from below:
                    </div>

                    <div>
                      {billingDetailsList?.map((mapObj) => (
                        <div
                          key={mapObj.billing_id}
                          className="my-3 lg:my-5"
                        >
                          <BillingAddressCard
                            billing_id={mapObj.billing_id}
                            first_name={mapObj.first_name}
                            last_name={mapObj.last_name}
                            address_line1={mapObj.address_line1}
                            address_line2={mapObj.address_line2}
                            landmark={mapObj.landmark}
                            city_name={mapObj.city_name}
                            state_name={mapObj.state_name}
                            pincode={mapObj.pincode}
                            country_name="India"
                            phone_number={mapObj.phone_number}
                            onBillingClickHandler={onBillingClickHandler}
                            selectedBillingId={selectedBillingId}
                            onClickEdit={onClickEdit}
                            setBillingDetailsDeleteId={setBillingDetailsDeleteId}
                            setShowDelete={setShowDelete}
                            mapObj={mapObj}
                          />
                        </div>
                      ))}

                      <div
                        onClick={onClickAdd}
                        className="pt-3 md:pt-6 pb-4 text-xs text-black cursor-pointer uppercase"
                      >
                        + Add New Billing Address
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm py-3">
                      {!isEdit ? (
                        <span className="uppercase">
                          Add a new billing address:
                        </span>
                      ) : (
                        <span className="uppercase">Edit billing address:</span>
                      )}
                    </div>

                    <BillingDetailsForm
                      register={register}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      defaultValues={defaultValues}
                      handleSubmit={handleSubmit}
                      onSubmit={onSubmitForm}
                      setShowAddAddressForm={setShowAddAddressForm}
                      setEditBillingAddressDetails={
                        setEditBillingAddressDetails
                      }
                      reset={reset}
                    />
                  </>
                )}
              </div>

              <DeleteModal
                open={showDelete}
                setOpen={setShowDelete}
                message="This billing address will be deleted. Are you sure?"
                onDelete={onClickDelete}
              />
            </div>

            <div className="col-span-1 border mt-8 md:mt-0 px-3 py-4">
              <div className="relative flex items-center">
                <span className="flex-shrink pr-3 text-sm lg:text-base">
                  Your Order
                </span>
                <div className="flex-grow border-t border-gray-400" />
              </div>

              <div className="mt-5 flex justify-between border-b border-gray-400">
                <p className="text-xs lg:text-sm text-gray-700">Product</p>
                <p className="text-xs lg:text-sm text-gray-700">Total</p>
              </div>

              <div className="border-b border-gray-400 pb-5">
                {cartList?.map((cartObj, index) => {
                  const itemTotal =
                    cartObj.discount_percentage === 0
                      ? cartObj.quantity * cartObj.product_rate
                      : cartObj.quantity *
                        (cartObj?.product_rate -
                          (cartObj?.discount_percentage / 100) *
                            cartObj?.product_rate);

                  return (
                    <div
                      key={`${cartObj?.cart_id || index}`}
                      className="mt-5 flex justify-between gap-x-3 lg:gap-x-0"
                    >
                      <p className="text-xs lg:text-sm">
                        {cartObj?.product_details?.product_name} (
                        {cartObj.size_name}) <span className="font-bold">x</span>{" "}
                        {cartObj.quantity}
                      </p>
                      <p className="text-xs lg:text-sm">₹ {itemTotal}</p>
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 pb-3 flex justify-between border-b border-gray-400">
                <p className="text-xs lg:text-sm text-gray-700">
                  Cart Subtotal
                </p>
                <p className="text-xs lg:text-sm">₹ {cartTotal}</p>
              </div>

              <div className="pt-2 pb-3 flex justify-between border-b border-gray-400">
                <p className="text-xs lg:text-sm text-gray-700">
                  Delivery Charges
                </p>
                <p className="text-xs lg:text-sm">₹ {deliverycharge}</p>
              </div>

              <div className="text-xs text-red-700 pt-3 pb-5">
                Delivery fee is non-refundable.
              </div>

              <div className="pt-2 pb-3 flex justify-between border-b border-gray-400">
                <p className="text-xs lg:text-sm text-gray-700">Order Total</p>
                <p className="text-xs lg:text-sm">₹ {orderTotal}</p>
              </div>

              {/*
                PAYMENT MODE SECTION HIDDEN FOR NOW

                To enable both COD + Online in future:
                1. Uncomment this entire block.
                2. Set the default paymentMode at the top as needed.

                To make Online-only in future:
                1. Uncomment this block if you want user choice, OR keep it hidden.
                2. Set:
                   const [paymentMode, setPaymentMode] = useState("Online");
                3. Optionally remove/comment COD radio option below.

                To make COD-only again:
                1. Keep this block commented.
                2. Keep:
                   const [paymentMode, setPaymentMode] = useState("COD");
              */}
              {/*
              <div className="pt-5">
                <div className="text-xs lg:text-sm text-gray-700 mb-3 uppercase">
                  Payment Mode
                </div>

                <div className="space-y-3 border border-gray-200 p-3">
                  <label className="flex items-center gap-2 cursor-pointer text-xs lg:text-sm">
                    <input
                      type="radio"
                      name="payment_mode"
                      value="Online"
                      checked={paymentMode === "Online"}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      className="cursor-pointer"
                    />
                    Online Payment
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs lg:text-sm">
                    <input
                      type="radio"
                      name="payment_mode"
                      value="COD"
                      checked={paymentMode === "COD"}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      className="cursor-pointer"
                    />
                    Cash on Delivery
                  </label>
                </div>
              </div>
              */}

              <div className="pt-6">
                  Payment Method: Cash on Delivery <br/>
                <p className="text-xs text-gray-700">
                  Click on the button below to place your order.
                </p>
              

                {selectedBillingId ? (
                  <>
                    {!processingPaymentMsg ? (
                      <div
                        onClick={onPlaceOrderClickHandler}
                        className="mt-4 flex justify-center cursor-pointer bg-white text-black text-xs border-[0.1px] border-black hover:bg-black hover:text-white transition-all ease-in-out duration-300 px-3 py-2 lg:px-5 lg:py-3 uppercase"
                      >
                        Place Order
                      </div>
                    ) : (
                      <div className="mt-4 flex gap-x-1 justify-center items-center bg-black text-white rounded-sm px-3 py-2">
                        <div className="flex justify-center text-xs uppercase">
                          Processing
                        </div>
                        <div className="pl-1">
                          <Spinner />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="mt-4 flex items-center justify-center gap-2 text-center 
                               text-xs lg:text-sm font-medium text-yellow-600 
                               border border-yellow-300 bg-yellow-50 
                               rounded-md px-4 py-2 lg:px-6 lg:py-3 
                               transition-all duration-300 ease-in-out shadow-sm"
                  >
                    <AiOutlineWarning className="w-5 h-5 text-yellow-500" />
                    Please select a billing address.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Checkout;